import 'lazysizes';
import { createApp } from 'vue';

import { hoistJsonScripts } from '@Foundation/utilities';

// Plugins for zone-level customizations
import loadZoneCustomizations from '@refapp/zone-plugin-loader';
import init from '@refapp/sisyphus';

// Sitecore-Vue bridge
import SitecoreVueAdapter from '@Foundation/sitecore/SitecoreVueAdapter';

// Helpers
import { requireAll } from '@Foundation/utilities/helpers';

// Foundation
import eventBus, { exposeGlobals } from '@refapp/eventbus-js';
import { background } from '@Foundation/directives/background/background';
import { bus } from '@Foundation/directives/bus/bus';
import { navigationHeight } from '@Foundation/directives/navigation-height/navigation-height';
import { tag } from '@Foundation/analyticsHandler/directives/tag';
import { toggleClass } from '@Foundation/directives/toggle-class/toggle-class';

// Feature
import CookieBanner from '@Feature/PageContentExt';
import GenericForm from '@Feature/GenericForm';
import HeaderLogo from '@Feature/HeaderLogo';
import Iframepopup from '@Feature/IframePopup';
import NavigationModule from '@Feature/Navigation';
import NewsletterModule from '@Feature/NewsLetter';
import OapAccountNavigation from '@Feature/OapAccountNavigation';
import OapBeautyGeniusWrapper from '@Feature/OapBeautyGeniusWrapper';
import OapCountdown from '@Feature/OapCountdown';
import OapCardPopin from '@Feature/OapCardPopin';
import OapEventfulTextInput from '@Feature/OapEventfulTextInput';
import OapFindationModule from '@Feature/OapFindation';
import OapIframe from '@Feature/OapIframe';
import OapPopin from '@Feature/OapPopin';
import OapPromoBar from '@Feature/OapPromoBar';
import OapRating from '@Feature/OapRating';
import OapSlider from '@Feature/OapSlider';
import OapStrikethroughScrolling from '@Feature/OapStrikethroughScrolling';
import OneTrustCookieHelper from '@Feature/OneTrust';
import Overlay from '@Feature/Overlay';
import Search from '@Feature/Search';
import SearchSuggestions from '@Feature/SearchSuggestions';
import Slider from '@Feature/Slider';
import VideoPlaceholder from '@Feature/VideoPlaceholder';
import OapPageNavigation from '@Feature/OapPageNavigation';
import OapCarouselControls from '@Feature/CarouselControls';
import FooterNewsletterSignupModal from '@Feature/FooterNewsletterSignupModal';
import FloatingSidebar from '@Feature/FloatingSidebar';
import OapMikMak from '@Feature/OapMikMak';
import OapRegisterNowPopup from '@Feature/OapRegisterNowPopup';
import WishList from '@Feature/WishList';

exposeGlobals();

const app = createApp({});
app.config.compilerOptions.whitespace = 'preserve';
app.config.performance = true;

// Register directives
Object.entries({
  ...NavigationModule.directives,
  ...Search.directives,
  background,
  bus,
  navigationHeight,
  tag,
  toggleClass,
}).forEach(([name, directive]) => app.directive(name, directive));

// Register components
Object.entries({
  ...CookieBanner.components,
  ...GenericForm.components,
  ...HeaderLogo.components,
  ...Iframepopup.components,
  ...NavigationModule.components,
  ...NewsletterModule.components,
  ...OapAccountNavigation.components,
  ...OapBeautyGeniusWrapper.components,
  ...OapCountdown.components,
  ...OapCardPopin.components,
  ...OapEventfulTextInput.components,
  ...OapFindationModule.components,
  ...OapIframe.components,
  ...OapRating.components,
  ...OapSlider.components,
  ...OapPopin.components,
  ...OapPromoBar.components,
  ...OapStrikethroughScrolling.components,
  ...OneTrustCookieHelper.components,
  ...Overlay.components,
  ...SearchSuggestions.components,
  ...Slider.components,
  ...VideoPlaceholder.components,
  ...OapPageNavigation.components,
  ...OapCarouselControls.components,
  ...FooterNewsletterSignupModal.components,
  ...FloatingSidebar.components,
  ...OapMikMak.components,
  ...OapRegisterNowPopup.components,
  ...WishList.components,
}).forEach(([name, component]) => app.component(name, component));

/**
 * Initialize any zone-level customizations.
 * Every zone/country can create their own Vue components and make them available
 * to their code through the use of a Vue.js Plugin
 *
 * @see https://vuejs.org/guide/reusability/plugins.html#writing-a-plugin
 * */
loadZoneCustomizations(app, {});
// Initialize third party loader
init();

requireAll(require.context('../../../Icons/', true, /\.svg$/));

document.addEventListener('lazybeforeunveil', (event) =>
  eventBus.emit('image.lazybeforeunveil', event)
);
window.RefAppMctb && app.use(window.RefAppMctb);
window.RefAppAmp && app.use(window.RefAppAmp);
window.RefAppStoreLocator && app.use(window.RefAppStoreLocator);
window.RefAppFaq && app.use(window.RefAppFaq);
window.RefAppRatingsReviews && app.use(window.RefAppRatingsReviews);
window.RefAppBackToTop && app.use(window.RefAppBackToTop);
window.RefAppUserRedirect && app.use(window.RefAppUserRedirect);

hoistJsonScripts();

SitecoreVueAdapter.initializeVueApp(app.mount.bind(app, '#main-container'));
